import { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { highlightTree } from '@lezer/highlight';
import { ButtonVariant } from 'app/components/core/Button';
import { CopyToClipboardButton } from 'app/components/composite/CopyToClipboardButton';
import { useTheme } from 'app/components/core/ThemeProvider';
import { SnowSQLLanguage } from 'app/lib/codemirror/grammars/snowsql/snowsql';
import { classHighlightStyles } from './HighlightStyles.js';

import * as styles from './SqlCodeViewer.css';

const cx = classnames.bind(styles);

export function SqlCodeViewer({
    className,
    sql,
    allowCopyToClipboard = false,
    hideLineNumbers = false,
    fullHeight = false,
    breakWord = false
}) {
    const [children, setChildren] = useState([]);
    const { themeClassname } = useTheme();
    const classes = cx('base', themeClassname, className, { scrollable: !fullHeight });

    useEffect(() => {
        const htmlTags = [];

        const callback = (content, className, from, to) => {
            htmlTags.push(
                <span className={className} key={`span-${from}-${to}`}>
                    {content}
                </span>
            );
        };

        const tree = SnowSQLLanguage.parser.parse(sql);
        let pos = 0;

        highlightTree(tree, classHighlightStyles, (from, to, classes) => {
            if (from > pos) {
                callback(sql.slice(pos, from), null, pos, from);
            }

            callback(sql.slice(from, to), classes, from, to);

            pos = to;
        });

        if (pos !== tree.length) {
            callback(sql.slice(pos, tree.length), null, pos, tree.length);
        }

        setChildren(htmlTags);
    }, [sql]);

    const lineNumbers = sql.split('\n').map((_, i) => <div key={`line-${i + 1}`}>{i + 1}</div>);

    return (
        <div className={classes}>
            <div className={styles.sqlView}>
                <div className={styles.content}>
                    {!hideLineNumbers && <div className={styles.lineNumbers}>{lineNumbers}</div>}
                    <pre className={cx({ breakWord })}>{children}</pre>
                </div>
            </div>
            {allowCopyToClipboard && (
                <CopyToClipboardButton
                    iconOnly
                    value={sql}
                    className={styles.copyButton}
                    variant={ButtonVariant.SECONDARY}
                    message="components:SqlCodeViewer.copySuccess"
                />
            )}
        </div>
    );
}
